import {
  BackwardIcon,
  ForwardIcon,
  PauseCircleIcon,
  PlayCircleIcon,
} from "@heroicons/react/24/outline";
import React, { memo } from "react";

interface PlaybackControlsProps {
  isPlaying: boolean;
  playbackSpeed: number;
  currentTime: number;
  minTime: number;
  maxTime: number;
  disabled?: boolean;
  onPlayPause: () => void;
  onPlaybackSpeedChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSkipBackward: () => void;
  onSkipForward: () => void;
  onTimelineChange: (time: number) => void;
}

export const PlaybackControls = memo<PlaybackControlsProps>(
  ({
    isPlaying,
    playbackSpeed,
    currentTime,
    minTime,
    maxTime,
    disabled = false,
    onPlayPause,
    onPlaybackSpeedChange,
    onSkipBackward,
    onSkipForward,
    onTimelineChange,
  }) => {
    return (
      <>
        {/* Playback Speed Dropdown */}
        <div className="absolute bottom-5 right-8">
          <label htmlFor="playback-speed-select" className="sr-only">
            Playback Speed
          </label>
          <select
            id="playback-speed-select"
            value={playbackSpeed}
            onChange={onPlaybackSpeedChange}
            disabled={disabled}
            className={`px-3 py-2 bg-transparent text-white border border-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            data-testid="playback-speed-select"
            aria-label="Select playback speed"
          >
            <option value={0.1}>0.1x</option>
            <option value={0.25}>0.25x</option>
            <option value={0.5}>0.5x</option>
            <option value={1}>1x</option>
            <option value={2}>2x</option>
          </select>
        </div>

        {/* Playback Controls */}
        <div className="absolute bottom-4 left-4 flex items-center gap-4">
          {/* Play Controls */}
          <div className="flex items-center justify-start gap-2 ml-2">
            <button
              onClick={onSkipBackward}
              disabled={disabled}
              className={`p-1 transition-colors text-white ${
                disabled
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:text-gray-300 cursor-pointer"
              }`}
              data-testid="skip-backward-button"
              aria-label="Skip backward 0.1 seconds"
            >
              <BackwardIcon className="w-6 h-6 stroke-2" />
            </button>

            <button
              onClick={onPlayPause}
              disabled={disabled}
              className={`p-1 transition-colors text-white ${
                disabled
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:text-gray-300 cursor-pointer"
              }`}
              data-testid="play-pause-button"
              aria-label={isPlaying ? "Pause playback" : "Play playback"}
            >
              {isPlaying ? (
                <PauseCircleIcon className="w-7 h-7 stroke-2" />
              ) : (
                <PlayCircleIcon className="w-7 h-7 stroke-2" />
              )}
            </button>

            <button
              onClick={onSkipForward}
              disabled={disabled}
              className={`p-1 transition-colors text-white ${
                disabled
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:text-gray-300 cursor-pointer"
              }`}
              data-testid="skip-forward-button"
              aria-label="Skip forward 0.1 seconds"
            >
              <ForwardIcon className="w-6 h-6 stroke-2" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center justify-start">
            <label htmlFor="timeline-slider" className="sr-only">
              Timeline position
            </label>
            <input
              id="timeline-slider"
              type="range"
              min={Math.round(minTime * 100)}
              max={Math.round(maxTime * 100)}
              value={Math.round(currentTime * 100)}
              onChange={(e) => {
                const newTime = Number(e.target.value) / 100;
                onTimelineChange(newTime);
              }}
              disabled={disabled}
              className={`rounded-lg appearance-none ${
                disabled ? "cursor-not-allowed" : "cursor-pointer"
              }`}
              style={{
                width: "318px",
                height: "5px",
                background: `linear-gradient(to right, #f97316 0%, #f97316 ${((currentTime - minTime) / (maxTime - minTime)) * 100}%, rgba(255,255,255,0.2) ${((currentTime - minTime) / (maxTime - minTime)) * 100}%, rgba(255,255,255,0.2) 100%)`,
                accentColor: "#fb6a07",
                opacity: disabled ? 0.5 : 1,
                WebkitAppearance: "none",
                appearance: "none",
              }}
              data-testid="timeline-slider"
              aria-label={`Timeline position: ${currentTime.toFixed(2)} seconds`}
              aria-valuemin={minTime}
              aria-valuemax={maxTime}
              aria-valuenow={currentTime}
              aria-valuetext={`${currentTime.toFixed(2)} seconds`}
            />
            <style>{`
              #timeline-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 10px;
                height: 10px;
                border-radius: 50%;
                background: #f97316;
                cursor: pointer;
              }
              #timeline-slider::-moz-range-thumb {
                width: 10px;
                height: 10px;
                border-radius: 50%;
                background: #f97316;
                cursor: pointer;
                border: none;
              }
            `}</style>
          </div>
        </div>
      </>
    );
  },
);

PlaybackControls.displayName = "PlaybackControls";
